// Compact service-status pills — dots only, full name + state via hover-card.
// Hides into the header overflow on narrow viewports without dominating chrome.

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { SERVICE_URLS, type HealthState, type ServiceName } from "../../hooks/useServiceHealth";

const DOT_CLASS: Record<HealthState, string> = {
  up: "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]",
  down: "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]",
  checking: "bg-amber-400 animate-pulse",
};

const STATE_LABEL: Record<HealthState, string> = {
  up: "Healthy",
  down: "Unreachable",
  checking: "Checking…",
};

interface Props {
  state: Record<ServiceName, HealthState>;
}

export function ServiceStatus({ state }: Props) {
  return (
    <div className="flex items-center gap-1.5">
      {(Object.entries(state) as [ServiceName, HealthState][]).map(([name, s]) => (
        <HoverCard key={name} openDelay={120}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              aria-label={`${name}: ${STATE_LABEL[s]}`}
              className="rounded-full p-1.5 hover:bg-accent transition-colors"
            >
              <span className={cn("block size-2.5 rounded-full", DOT_CLASS[s])} />
            </button>
          </HoverCardTrigger>
          <HoverCardContent className="w-auto px-3 py-2 text-xs font-mono" align="end">
            <div className="font-semibold text-foreground">{name}</div>
            <div className="text-muted-foreground">{STATE_LABEL[s]}</div>
            {SERVICE_URLS[name] && (
              <div className="text-muted-foreground/70 truncate max-w-[260px] mt-1">{SERVICE_URLS[name]}</div>
            )}
          </HoverCardContent>
        </HoverCard>
      ))}
    </div>
  );
}
